// Standings engine — shared by the public app and the API.
//
// Moved verbatim (behavior-preserving) out of apps/app/app/utils/statParser.ts,
// which now re-exports `createStandings` so `statParser.createStandings(...)`
// keeps working for every existing caller.
//
// It lives here because TWO consumers must produce the SAME ordering:
//   • apps/app  — the public division Standings table, and the regular-season
//     rank fallback used when a playoff tie is awarded by seed.
//   • apps/api  — bracketAdvancement.ts, which needs that identical rank to
//     decide which team advances out of a tied bracket game.
// If the two ever computed rank differently, the page would highlight one team
// while the bracket advanced the other. See ADR 0029.
//
// Known pre-existing bugs, deliberately NOT fixed in the move so the extraction
// stays a pure relocation. Fix them in their own change — both alter output:
//   1. The tie branch credits `pa` twice to the HOME team
//      (`board[home].pa += game.homeScore` should be `board[away].pa`).
//   2. `calculateHeadToHeadRecord` is dead — `calculateHeadToHeadRecordAgainstTiedTeams`
//      is what the sort actually uses.
import _ from 'lodash';

/**
 * Build the ordered standings rows for a set of games.
 *
 * @param games  Games to tabulate. Callers pass REGULAR-SEASON games only —
 *               playoff games are excluded upstream.
 * @param config Effective (hierarchy-resolved) config; `config.tiebreakers[sport]`
 *               drives the tiebreaker chain and the per-game PD cap.
 * @param sport  Sport id. The full tiebreaker chain is basketball-only today;
 *               every other sport falls through to the default win/pd/pa sort.
 * @returns Rows ordered best-first. Each row carries `_id` (the DivisionTeam id)
 *          plus `name`, `win`/`loss`/`tie`, `pd`, `pa`, `players`, `division`,
 *          `subDivision`.
 */
// Games/config/rows are dynamic Mongoose/API shapes (populated team docs, ad-hoc
// row fields added during tiebreak computation) — typed as `any` deliberately.
export function createStandings(games: any[] = [], config: any = {}, sport = 'basketball'): any[] {
  // Per-game point-differential cap comes from the sport's tiebreaker config
  // (`config.tiebreakers[sport]` → pointDifferential.maxPointDifferential). null =
  // NO cap (raw differential). This previously defaulted to a hardcoded 15, which
  // capped PD even when no tiebreaker cap was ever configured.
  const pdTiebreaker = (_.get(config, `tiebreakers.${sport}`) || [])
    .find((t: any) => (typeof t === 'string' ? t === 'pointDifferential' : t.id === 'pointDifferential'));
  const maxDiff = pdTiebreaker && pdTiebreaker.maxPointDifferential != null
    ? pdTiebreaker.maxPointDifferential
    : null;

  const cloned = _.cloneDeep(games);

  /*
   {
   'Midlife Crisis': {win: 2,lose: 3,name: 'Midlife Crisis, id: '567abc'}
   }
   */
  const board: Record<string, any> = {};
  const filteredGames = _.filter(cloned, (game: any) => game.homeTeam && game.awayTeam);
  _.map(filteredGames, (game: any) => {
    board[game.homeTeam.name] = board[game.homeTeam.name] || {
      win: 0,
      tie: 0,
      loss: 0,
      pd: 0,
      pa: 0,
      name: game.homeTeam.name,
      _id: game.homeTeam._id,
      // Carry players so auto-generated (zqtm_) team names can resolve to
      // player names in the standings, matching the schedule/other views.
      players: game.homeTeam.players,
    };
    board[game.awayTeam.name] = board[game.awayTeam.name] || {
      win: 0,
      tie: 0,
      loss: 0,
      pd: 0,
      pa: 0,
      name: game.awayTeam.name,
      _id: game.awayTeam._id,
      players: game.awayTeam.players,
    };

    let pd = 0;
    if (!game.homeScore && !game.awayScore) {
      // No score recorded — the game contributes nothing.
    } else if (game.homeScore > game.awayScore) {
      pd = game.homeScore - game.awayScore;
      if (maxDiff != null && pd > maxDiff) pd = maxDiff;

      board[game.homeTeam.name].win++;
      board[game.homeTeam.name].pd += pd;
      board[game.homeTeam.name].pa += game.awayScore;

      board[game.awayTeam.name].loss++;
      board[game.awayTeam.name].pd -= pd;
      board[game.awayTeam.name].pa += game.homeScore;
    } else if (game.awayScore > game.homeScore) {
      pd = game.awayScore - game.homeScore;
      if (maxDiff != null && pd > maxDiff) pd = maxDiff;

      board[game.awayTeam.name].win++;
      board[game.awayTeam.name].pd += pd;
      board[game.awayTeam.name].pa += game.homeScore;

      board[game.homeTeam.name].loss++;
      board[game.homeTeam.name].pd -= pd;
      board[game.homeTeam.name].pa += game.awayScore;
    } else if (game.homeScore === game.awayScore) {
      // tie game
      board[game.homeTeam.name].tie++;
      board[game.homeTeam.name].pa += game.awayScore;

      board[game.awayTeam.name].tie++;
      // NOTE: pre-existing bug (see header) — should be board[away].pa.
      board[game.homeTeam.name].pa += game.homeScore;
    }

    board[game.homeTeam.name].winRate = board[game.homeTeam.name].win / (board[game.homeTeam.name].win + board[game.homeTeam.name].loss);
    board[game.homeTeam.name].division = game.division;
    board[game.awayTeam.name].division = game.division;
    board[game.homeTeam.name].subDivision = game.subDivision;
    board[game.awayTeam.name].subDivision = game.subDivision;
  });

  // Convert board object to array for sorting
  let standings: any[] = Object.values(board);

  // Apply the sport's configured tiebreaker chain (config.tiebreakers[sport] —
  // basketball, volleyball, soccer, … all share the points-based machinery; for
  // volleyball "points" are sets won). An EMPTY array means "no tiebreakers
  // configured" — it must fall through to the default sort below (which breaks
  // ties by PD desc), not enter this branch with zero tiebreakers (which would
  // leave same-record teams unsorted).
  const sportTiebreakers = _.get(config, `tiebreakers.${sport}`);
  if (sportTiebreakers && sportTiebreakers.length > 0) {
    const tiebreakers = sportTiebreakers;

    // Handle backward compatibility: convert string array to object array if needed
    const normalizedTiebreakers = tiebreakers.map((tiebreaker: any) => {
      if (typeof tiebreaker === 'string') {
        return { id: tiebreaker };
      }
      return tiebreaker;
    });

    // Find maxPointsScored configuration for pointsScored tiebreaker
    const pointsScoredConfig = normalizedTiebreakers.find((t: any) => t.id === 'pointsScored');
    const maxPointsScored = pointsScoredConfig?.maxPointsScored;

    // Find maxPointDifferential configuration for pointDifferential tiebreaker
    const pointDifferentialConfig = normalizedTiebreakers.find((t: any) => t.id === 'pointDifferential');
    const maxPointDifferential = pointDifferentialConfig?.maxPointDifferential;

    // Calculate additional tiebreaker values for each team
    standings.forEach((team) => {
      // Calculate points scored and allowed totals
      team.pointsScored = 0;
      team.pointsAllowed = 0;
      team.gamesPlayed = 0;
      team.pointDifferential = 0; // Initialize point differential

      filteredGames.forEach((game: any) => {
        if (game.homeTeam.name === team.name) {
          if (game.homeScore !== undefined && game.homeScore !== null) {
            // Apply max points scored limit if configured
            let { homeScore } = game;
            if (maxPointsScored !== null && maxPointsScored !== undefined && homeScore > maxPointsScored) {
              homeScore = maxPointsScored;
            }
            team.pointsScored += homeScore;
            team.pointsAllowed += game.awayScore || 0;
            team.gamesPlayed++;

            // Calculate point differential for this game and apply per-game limit
            let gamePointDiff = homeScore - (game.awayScore || 0);
            if (maxPointDifferential !== null && maxPointDifferential !== undefined) {
              if (gamePointDiff > maxPointDifferential) {
                gamePointDiff = maxPointDifferential;
              } else if (gamePointDiff < -maxPointDifferential) {
                gamePointDiff = -maxPointDifferential;
              }
            }
            team.pointDifferential += gamePointDiff;
          }
        } else if (game.awayTeam.name === team.name) {
          if (game.awayScore !== undefined && game.awayScore !== null) {
            // Apply max points scored limit if configured
            let { awayScore } = game;
            if (maxPointsScored !== null && maxPointsScored !== undefined && awayScore > maxPointsScored) {
              awayScore = maxPointsScored;
            }
            team.pointsScored += awayScore;
            team.pointsAllowed += game.homeScore || 0;
            team.gamesPlayed++;

            // Calculate point differential for this game and apply per-game limit
            let gamePointDiff = awayScore - (game.homeScore || 0);
            if (maxPointDifferential !== null && maxPointDifferential !== undefined) {
              if (gamePointDiff > maxPointDifferential) {
                gamePointDiff = maxPointDifferential;
              } else if (gamePointDiff < -maxPointDifferential) {
                gamePointDiff = -maxPointDifferential;
              }
            }
            team.pointDifferential += gamePointDiff;
          }
        }
      });

      // Calculate per-game averages
      team.pointsScoredPerGame = team.gamesPlayed > 0 ? team.pointsScored / team.gamesPlayed : 0;
      team.pointsAllowedPerGame = team.gamesPlayed > 0 ? team.pointsAllowed / team.gamesPlayed : 0;
      team.pointsDifferentialPerGame = team.pointsScoredPerGame - team.pointsAllowedPerGame;
    });

    // With a head-to-head tiebreaker configured, recalculate head-to-head against tied teams only
    if (sportTiebreakers) {
      const headToHeadTiebreaker = sportTiebreakers.find((t: any) => (typeof t === 'string' ? t === 'headToHead' : t.id === 'headToHead'));

      if (headToHeadTiebreaker) {
        // Group teams by their win/loss record
        const teamsByRecord: Record<string, any[]> = {};
        standings.forEach((team) => {
          const recordKey = `${team.win}-${team.loss}`;
          if (!teamsByRecord[recordKey]) {
            teamsByRecord[recordKey] = [];
          }
          teamsByRecord[recordKey].push(team);
        });

        // For each group of tied teams, recalculate head-to-head against only those teams
        Object.values(teamsByRecord).forEach((tiedTeams) => {
          if (tiedTeams.length > 1) {
            tiedTeams.forEach((team) => {
              const h2hRecord = calculateHeadToHeadRecordAgainstTiedTeams(team, tiedTeams, filteredGames);
              team.headToHead = h2hRecord;

              // Also calculate and store the actual wins and losses for display
              let wins = 0;
              let losses = 0;
              tiedTeams.forEach((otherTeam) => {
                if (otherTeam.name !== team.name) {
                  const { wins: h2hWins, losses: h2hLosses } = getHeadToHeadRecord(team, otherTeam, filteredGames);
                  wins += h2hWins;
                  losses += h2hLosses;
                }
              });
              team.headToHeadWins = wins;
              team.headToHeadLosses = losses;
            });
          }
        });
      }
    }

    // Custom sorting function to handle head-to-head tiebreakers
    const customSort = (a: any, b: any) => {
      // First, sort by win/loss record
      if (a.win !== b.win) return b.win - a.win;
      if (a.loss !== b.loss) return a.loss - b.loss;

      // If tied, apply tiebreakers in order
      for (let i = 0; i < normalizedTiebreakers.length; i++) {
        const tiebreaker = normalizedTiebreakers[i];

        if (tiebreaker.id === 'headToHead') {
          // For head-to-head, we need to find all teams with the same record
          // and calculate head-to-head only against those teams
          const sameRecordTeams = standings.filter((team) => team.win === a.win && team.loss === a.loss);

          if (sameRecordTeams.length > 1) {
            const aH2H = calculateHeadToHeadRecordAgainstTiedTeams(a, sameRecordTeams, filteredGames);
            const bH2H = calculateHeadToHeadRecordAgainstTiedTeams(b, sameRecordTeams, filteredGames);

            if (aH2H !== bH2H) {
              return bH2H - aH2H; // Higher head-to-head record wins
            }
          }
        } else {
          const aValue = getTiebreakerValue(a, tiebreaker.id);
          const bValue = getTiebreakerValue(b, tiebreaker.id);

          if (aValue !== bValue) {
            // For pointsAllowed, lower values should rank higher (ascending order)
            // For all other tiebreakers, higher values should rank higher (descending order)
            if (tiebreaker.id === 'pointsAllowed') {
              return aValue - bValue; // Ascending: fewer points allowed = better
            }
            return bValue - aValue; // Descending: higher values = better
          }
        }
      }

      return 0; // If all tiebreakers are equal
    };

    standings.sort(customSort);
  } else {
    // Default sorting when the sport has no tiebreaker chain configured
    standings = _.orderBy(standings, ['win', 'pd', 'pa'], ['desc', 'desc', 'asc']);
  }

  return standings;
}

// Helper function to get tiebreaker value
export function getTiebreakerValue(team: any, tiebreaker: string): number {
  switch (tiebreaker) {
    case 'pointsScored':
      return team.pointsScored || 0;
    case 'pointDifferential':
      return team.pointDifferential || 0;
    case 'pointsAllowed':
      return team.pointsAllowed || 0;
    case 'headToHead':
      // For head-to-head, we'll calculate this dynamically in the sorting function
      // This is just a fallback for backward compatibility
      return team.headToHead || 0;
    default:
      return 0;
  }
}

// Helper function to calculate head-to-head record for a team against only the tied teams
export function calculateHeadToHeadRecordAgainstTiedTeams(team: any, tiedTeams: any[], games: any[]): number {
  let totalWins = 0;
  let totalLosses = 0;

  // Only calculate head-to-head against teams that are tied
  tiedTeams.forEach((otherTeam) => {
    if (otherTeam.name !== team.name) {
      const { wins, losses } = getHeadToHeadRecord(team, otherTeam, games);
      totalWins += wins;
      totalLosses += losses;
    }
  });

  // Return a numeric value representing the head-to-head record
  // This can be used for sorting (higher is better)
  return totalWins - totalLosses;
}

// Helper function to get head-to-head record between two specific teams
export function getHeadToHeadRecord(teamA: any, teamB: any, games: any[]): { wins: number; losses: number } {
  let teamAWins = 0;
  let teamBWins = 0;

  games.forEach((game) => {
    if (game.homeScore !== undefined && game.homeScore !== null
        && game.awayScore !== undefined && game.awayScore !== null) {
      const isTeamAHome = game.homeTeam.name === teamA.name;
      const isTeamAway = game.awayTeam.name === teamA.name;
      const isTeamBHome = game.homeTeam.name === teamB.name;
      const isTeamBAway = game.awayTeam.name === teamB.name;

      // Check if this is a head-to-head game between these two teams
      if ((isTeamAHome && isTeamBAway) || (isTeamAway && isTeamBHome)) {
        const teamAScore = isTeamAHome ? game.homeScore : game.awayScore;
        const teamBScore = isTeamBHome ? game.homeScore : game.awayScore;

        if (teamAScore > teamBScore) {
          teamAWins++;
        } else if (teamBScore > teamAScore) {
          teamBWins++;
        }
        // Ties are ignored in head-to-head comparison
      }
    }
  });

  return { wins: teamAWins, losses: teamBWins };
}

export default createStandings;
